import { Injectable } from '@angular/core';

import { BaseGuard } from '@guards/base.guard';
import { StockView } from '@modules/SDKs/sysnetApi/model/stockView';
import { VenuesService } from '@services/sysnet/venues.service';
import { SysnetCloudConstants } from 'src/app/Constants';

import {
  StockMobileStepperModule,
  IStockMobileStepperConfig,
  StockMobileStepperConfigs
} from '@components/dashboard/apps/sysnet/stock-mobile/stock-mobile-stepper/stock-mobile-stepper-configs';
import logger from '@hal.common.ui/utilities/Logger';

@Injectable()
export class StockMobileService {

  public commonHeaderTitle = '';
  public showStepper = false;
  public totalSteps = 0;
  public currentStep = 0;
  private currentModule: StockMobileStepperModule | null = null;

  // Current stepper configuration cache
  private currentStepperConfig: IStockMobileStepperConfig | null = null;

  private stockDetails: StockView;
  private showHeaderExtraContent = false;
  private stockGroupHeader = false;
  private locationName = '';
  private destinationLocationName = '';
  private sourceVenueName = '';
  private destinationVenueName = '';
  private stockGroupCount = 0;
  private selectedStocktakeId: number | null = null;
  private selectedDiscrepancyId: number | null = null;
  private selectedTransferId: number | null = null;
  private previousUrl: string[] | null = null;
  private listTabs: Record<string, string | null> = {};

  // Venue permissions cache
  private accessibleVenueIds: Set<string> = new Set();

  constructor(
    private venuesService: VenuesService,
    private baseGuard: BaseGuard
  ) {}

  public setLastSelectedLocation(locationId: number): void {
    localStorage.setItem('stockMobileSelectedLocationId', locationId.toString());
  }

  public getLastSelectedLocation(): number {
    return Number(localStorage.getItem('stockMobileSelectedLocationId'));
  }

  public setLastSelectedLocationName(locationName: string): void {
    localStorage.setItem('stockMobileSelectedLocationName', locationName);
  }

  public getLastSelectedLocationName(): string {
    return localStorage.getItem('stockMobileSelectedLocationName');
  }

  public setLastStockDetails(stockDetails: StockView): void {
    this.stockDetails = stockDetails;
  }

  public getLastStockDetails(): StockView {
    return this.stockDetails;
  }

  public setShowHeaderExtraContent(showHeaderExtraContent: boolean): void {
    this.showHeaderExtraContent = showHeaderExtraContent;
  }

  public hasHeaderExtraContent(): boolean {
    return this.showHeaderExtraContent;
  }

  public setStockGroupHeaderVisible(value: boolean): void {
    this.stockGroupHeader = value;
  }

  public isStockGroupHeaderVisible(): boolean {
    return this.stockGroupHeader;
  }

  public setLocation(locationName: string): void {
    this.locationName = locationName;
  }

  public setDestinationLocation(locationName: string): void {
    this.destinationLocationName = locationName;
  }

  public setSourceVenueName(sourceVenueName: string): void {
    this.sourceVenueName = sourceVenueName;
  }

  public setDestinationVenueName(destinationVenueName: string): void {
    this.destinationVenueName = destinationVenueName;
  }

  public setStockGroupCount(stockGroupCount: number): void {
    this.stockGroupCount = stockGroupCount;
  }

  public getLocation(): string {
    return this.locationName;
  }

  public getDestinationLocation(): string {
    return this.destinationLocationName;
  }

  public getSourceVenueName(): string {
    return this.sourceVenueName;
  }

  public getDestinationVenueName(): string {
    return this.destinationVenueName;
  }

  public getStockGroupCount(): number {
    return this.stockGroupCount;
  }

  public setSelectedStocktakeId(stocktakeId: number | null): void {
    this.selectedStocktakeId = stocktakeId;
  }

  public getSelectedStocktakeId(): number | null {
    return this.selectedStocktakeId;
  }

  public setSelectedDiscrepancyId(discrepancyId: number | null): void {
    this.selectedDiscrepancyId = discrepancyId;
  }

  public getSelectedDiscrepancyId(): number | null {
    return this.selectedDiscrepancyId;
  }

  public setSelectedTransferId(transferId: number | null): void {
    this.selectedTransferId = transferId;
  }

  public getSelectedTransferId(): number | null {
    return this.selectedTransferId;
  }

  public setTransferSourceLocationId(locationId: number | null): void {
    if (locationId) {
      localStorage.setItem('transferSourceLocationId', locationId.toString());
    } else {
      localStorage.removeItem('transferSourceLocationId');
    }
  }

  public getTransferSourceLocationId(): number | null {
    const storedId = localStorage.getItem('transferSourceLocationId');
    return storedId ? +storedId : null;
  }

  public setTransferSourceLocationName(locationName: string | null): void {
    if (locationName) {
      localStorage.setItem('transferSourceLocationName', locationName.toString());
    } else {
      localStorage.removeItem('transferSourceLocationName');
    }
  }

  public getTransferSourceLocationName(): string | null {
    const storedName = localStorage.getItem('transferSourceLocationName');
    return storedName ? storedName : null;
  }

  public setTransferSourceVenueName(name: string | null): void {
    if (name) {
      localStorage.setItem('transferSourceVenueName', name.toString());
    } else {
      localStorage.removeItem('transferSourceVenueName');
    }
  }

  public getTransferSourceVenueName(): string | null {
    const storedId = localStorage.getItem('transferSourceVenueName');
    return storedId ? storedId : null;
  }

  public setTransferDestinationLocationId(locationId: number | null): void {
    if (locationId) {
      localStorage.setItem('transferDestinationLocationId', locationId.toString());
    } else {
      localStorage.removeItem('transferDestinationLocationId');
    }
  }

  public getTransferDestinationLocationId(): number | null {
    const storedId = localStorage.getItem('transferDestinationLocationId');
    return storedId ? +storedId : null;
  }

  public setTransferDestinationLocationName(locationName: string | null): void {
    if (locationName) {
      localStorage.setItem('transferDestinationLocationName', locationName.toString());
    } else {
      localStorage.removeItem('transferDestinationLocationName');
    }
  }

  public getTransferDestinationLocationName(): string | null {
    const storedName = localStorage.getItem('transferDestinationLocationName');
    return storedName ? storedName : null;
  }

  public setTransferDestinationVenueName(name: string | null): void {
    if (name) {
      localStorage.setItem('transferDestinationVenueName', name.toString());
    } else {
      localStorage.removeItem('transferDestinationVenueName');
    }
  }

  public getTransferDestinationVenueName(): string | null {
    const storedId = localStorage.getItem('transferDestinationVenueName');
    return storedId ? storedId : null;
  }

  /**
   * Initialize stepper for a specific module
   * @param module The module that will use the stepper
   * @param initialStep The initial step (default: 0)
   */
  public initializeStepper(module: StockMobileStepperModule, initialStep = 0): void {
    this.currentModule = module;
    this.currentStepperConfig = StockMobileStepperConfigs.getModuleConfig(module);
    this.totalSteps = this.currentStepperConfig.totalSteps;
    this.currentStep = initialStep;
    this.showStepper = true;
  }

  /**
   * Update the current step
   * @param step The step to set as current
   */
  public setCurrentStep(step: number): void {
    if (this.currentModule && step >= 0 && step < this.totalSteps) {
      this.currentStep = step;
    }
  }

  /**
   * Move to the next step
   */
  public nextStep(): void {
    if (this.currentModule && this.currentStep < this.totalSteps - 1) {
      this.currentStep += 1;
    }
  }

  /**
   * Move to the previous step
   */
  public previousStep(): void {
    if (this.currentModule && this.currentStep > 0) {
      this.currentStep -= 1;
    }
  }

  /**
   * Hide the stepper
   */
  public hideStepper(): void {
    this.showStepper = false;
    this.currentModule = null;
    this.currentStepperConfig = null;
    this.totalSteps = 0;
    this.currentStep = 0;
  }

  /**
   * Get the current module's stepper configuration
   */
  public getCurrentStepperConfig(): IStockMobileStepperConfig | null {
    return this.currentStepperConfig;
  }

  /**
   * Get the current module
   */
  public getCurrentModule(): StockMobileStepperModule | null {
    return this.currentModule;
  }

  /**
   * Get the next route for the current step
   * @returns Array of route segments or null if no next route
   */
  public getNextRoute(): string[] | null {
    if (!this.currentModule) {
      return null;
    }

    return StockMobileStepperConfigs.getNextRoute(this.currentModule, this.currentStep);
  }

  /**
   * Navigate to the next step and return the route
   * Handles final step actions (save + navigate)
   * @returns Array of route segments or null if no next route
   */
  public navigateToNextStep(): string[] | null {
    if (!this.currentModule) {
      return null;
    }

    const nextRoute = this.getNextRoute();

    if (nextRoute) {
      // Advance to next step if not on the last step
      this.nextStep();
    }

    return nextRoute;
  }

  /**
   * Check if current step is the final step
   */
  public isFinalStep(): boolean {
    if (!this.currentModule) {
      return false;
    }

    return StockMobileStepperConfigs.isFinalStep(this.currentModule, this.currentStep);
  }

  /**
   * Set the previous URL for back navigation
   */
  public setPreviousUrl(url: string[] | null): void {
    this.previousUrl = url;
  }

  /**
   * Get the previous URL for back navigation
   */
  public getPreviousUrl(): string[] | null {
    return this.previousUrl;
  }

  /**
   * Clear the previous URL
   */
  public clearPreviousUrl(): void {
    this.previousUrl = null;
  }

  public setLastListTab(moduleKey: string, tab: string): void {
    if (!moduleKey || !tab) {
      return;
    }

    this.listTabs[moduleKey] = tab;
    localStorage.setItem(`stockMobileLastListTab:${moduleKey}`, tab);
  }

  public getLastListTab(moduleKey: string): string | null {
    if (!moduleKey) {
      return null;
    }

    if (this.listTabs[moduleKey] !== null && this.listTabs[moduleKey] !== undefined) {
      return this.listTabs[moduleKey];
    }

    const stored = localStorage.getItem(`stockMobileLastListTab:${moduleKey}`);
    if (stored) {
      this.listTabs[moduleKey] = stored;
      return stored;
    }

    return null;
  }

  public clearLastListTab(moduleKey: string): void {
    if (!moduleKey) {
      return;
    }

    this.listTabs[moduleKey] = null;
    localStorage.removeItem(`stockMobileLastListTab:${moduleKey}`);
  }

  /**
   * Pre-loads all venues that the user has delete permission for
   * This is cached and only loaded once per session
   */
  public async loadVenuePermissions(): Promise<void> {
    try {
      this.accessibleVenueIds.clear();
      // Get all venues user has access to
      const venuesResponse = await this.venuesService.searchVenues();
      const allVenues = venuesResponse.body || [];

      // Check delete permission for each venue
      const venuePermissionChecks = allVenues.map(async (venue) => {
        try {
          const hasAccess = await this.baseGuard.checkPermissions(
            SysnetCloudConstants.DATA_ACCESS.DELETE_TRANSFER_HEADERS,
            undefined,
            { _id: venue.venueId.toString() }
          ) === true;

          if (hasAccess) {
            this.accessibleVenueIds.add(venue.venueId);
          }
        } catch (error) {
          logger.error('An unexpected error occurred while loading venue permissions. Err: ' + error);
        }
      });

      await Promise.all(venuePermissionChecks);
    } catch (err) {
      logger.error('An unexpected error occurred while loading venue permissions. Err: ' + err);
    }
  }

  /**
   * Get accessible venue IDs
   */
  public getAccessibleVenueIds(): Set<string> {
    return this.accessibleVenueIds;
  }
}
